import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { usePreferences } from "../context/PreferencesContext";
import { getAvaaioAuthCopy, localizeAuthResponse, } from "../utils/avaaioAuthCopy";
import { normalizeAvaaioLanguage, } from "../utils/avaaioLanguage";
import { useEffect, useMemo, useState, } from "react";
import "../styles/AvaaioStarRating.css";
const DEFAULT_API_BASE = import.meta.env.VITE_API_BASE || "";
const PENDING_RATING_KEY = "avaaio_pending_star_rating";
const ratingLabels = {
    1: "Needs improvement",
    2: "Fair",
    3: "Good",
    4: "Excellent",
    5: "Incredible, truly amazing",
};
const normalizedApiBase = (apiBase) => {
    return String(apiBase || "").replace(/\/$/, "");
};
// __AVAAIO_LOGIN_RATING_SUBMIT_LAUNCH_COUNT_PHASE1__
const AVAAIO_LOGIN_RATING_BUILD = "verified-waitlist-phase2a-candidate";
export async function submitPendingAvaaioRating(apiBase = DEFAULT_API_BASE, token = "") {
    let pendingRating = 0;
    try {
        pendingRating = Number(sessionStorage.getItem(PENDING_RATING_KEY) || "0");
    }
    catch {
        return {
            outcome: "none",
        };
    }
    if (!Number.isInteger(pendingRating) ||
        pendingRating < 1 ||
        pendingRating > 5) {
        return {
            outcome: "none",
        };
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 4500);
    try {
        const response = await fetch(`${normalizedApiBase(apiBase)}/api/v1/auth/ratings`, {
            method: "POST",
            credentials: "include",
            signal: controller.signal,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...(token
                    ? {
                        Authorization: `Bearer ${token}`,
                    }
                    : {}),
            },
            body: JSON.stringify({
                rating: pendingRating,
            }),
        });
        if (response.status === 401 ||
            response.status === 403) {
            return {
                outcome: "signin_required",
            };
        }
        if (!response.ok) {
            return {
                outcome: "failed",
            };
        }
        const data = await response.json();
        try {
            sessionStorage.removeItem(PENDING_RATING_KEY);
        }
        catch { }
        return {
            outcome: "submitted",
            summary: data?.summary,
        };
    }
    catch {
        return {
            outcome: "failed",
        };
    }
    finally {
        window.clearTimeout(timeout);
    }
}
const AvaaioStarRating = () => {
    const [summary, setSummary] = useState(null);
    const [selectedRating, setSelectedRating] = useState(0);
    const { preferences } = usePreferences();
    const copy = getAvaaioAuthCopy(preferences.language);
    const ratingCopy = copy.rating;
    const [hoveredRating, setHoveredRating] = useState(0);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const [launchInterest, setLaunchInterest,] = useState(null);
    const [waitlistEmail, setWaitlistEmail] = useState("");
    const [waitlistLanguage, setWaitlistLanguage,] = useState(() => normalizeAvaaioLanguage(preferences.language));
    const [waitlistConsent, setWaitlistConsent] = useState(false);
    useEffect(() => {
        setWaitlistLanguage(normalizeAvaaioLanguage(preferences.language));
    }, [preferences.language]);
    const [waitlistBusy, setWaitlistBusy] = useState(false);
    const [waitlistMessage, setWaitlistMessage] = useState("");
    const activeRating = hoveredRating || selectedRating;
    const activeLabel = useMemo(() => {
        return activeRating
            ? ratingCopy.labels[activeRating]
            : ratingCopy.select;
    }, [activeRating, ratingCopy]);
    const loadSummary = async () => {
        try {
            const response = await fetch(`${normalizedApiBase(DEFAULT_API_BASE)}/api/v1/auth/ratings/summary`, {
                credentials: "include",
                cache: "no-store",
                headers: {
                    Accept: "application/json",
                },
            });
            if (!response.ok)
                return;
            const data = await response.json();
            setSummary({
                average: Number(data?.average || 0),
                rating_count: Number(data?.rating_count || 0),
                distribution: data?.distribution || {},
            });
        }
        catch {
            // The rating control remains available even
            // if the public summary cannot be loaded.
        }
    };
    const loadLaunchInterest = async () => {
        try {
            const response = await fetch(`${normalizedApiBase(DEFAULT_API_BASE)}/api/v1/auth/launch-interest`, {
                credentials: "include",
                cache: "no-store",
                headers: {
                    Accept: "application/json",
                },
            });
            if (!response.ok)
                return;
            const data = await response.json();
            const interestedCount = Number(data?.interested_count || 0);
            const launchStatus = String(data?.launch_status || "prelaunch").toLowerCase() === "launched"
                ? "launched"
                : "prelaunch";
            const waitlistOpen = typeof data?.waitlist_open === "boolean"
                ? data.waitlist_open
                : launchStatus === "prelaunch";
            setLaunchInterest({
                interested_count: Number.isFinite(interestedCount) &&
                    interestedCount >= 0
                    ? Math.floor(interestedCount)
                    : 0,
                launch_date: String(data?.launch_date ||
                    "2026-12-13"),
                launch_status: launchStatus,
                waitlist_open: waitlistOpen,
            });
        }
        catch {
            // The login and rating controls remain usable
            // if the aggregate launch count is unavailable.
        }
    };
    useEffect(() => {
        void loadSummary();
        void loadLaunchInterest();
        try {
            const pending = Number(sessionStorage.getItem(PENDING_RATING_KEY) || "0");
            if (Number.isInteger(pending) &&
                pending >= 1 &&
                pending <= 5) {
                setSelectedRating(pending);
                setMessage(ratingCopy.signInSaved);
            }
        }
        catch { }
    }, []);
    const chooseRating = (rating) => {
        if (busy)
            return;
        setSelectedRating(rating);
        try {
            sessionStorage.setItem(PENDING_RATING_KEY, String(rating));
        }
        catch { }
        setMessage(ratingCopy.selected);
    };
    const submitSelectedRating = async () => {
        if (busy ||
            !Number.isInteger(selectedRating) ||
            selectedRating < 1 ||
            selectedRating > 5) {
            return;
        }
        try {
            sessionStorage.setItem(PENDING_RATING_KEY, String(selectedRating));
        }
        catch { }
        setBusy(true);
        setMessage("");
        try {
            const result = await submitPendingAvaaioRating();
            if (result.outcome === "submitted") {
                setMessage(ratingCopy.thankYou);
                if (result.summary) {
                    setSummary(result.summary);
                }
                else {
                    await loadSummary();
                }
            }
            else if (result.outcome === "signin_required") {
                setMessage(ratingCopy.signInRegister);
            }
            else if (result.outcome === "failed") {
                setMessage(ratingCopy.savedAfterSignIn);
            }
        }
        finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        try {
            const status = new URLSearchParams(window.location.search).get("waitlist");
            if (status === "verified") {
                setWaitlistMessage(ratingCopy.verifiedWaitlist);
            }
            else if (status === "invalid") {
                setWaitlistMessage(ratingCopy.invalidWaitlist);
            }
        }
        catch {
            // Query-string feedback is optional.
        }
    }, []);
    const submitWaitlist = async (event) => {
        event.preventDefault();
        const email = waitlistEmail
            .trim()
            .toLowerCase();
        if (!email) {
            setWaitlistMessage(ratingCopy.enterEmail);
            return;
        }
        if (!waitlistConsent) {
            setWaitlistMessage(ratingCopy.consentRequired);
            return;
        }
        setWaitlistBusy(true);
        setWaitlistMessage("");
        try {
            const response = await fetch(`${normalizedApiBase(DEFAULT_API_BASE)}/api/v1/auth/waitlist/join`, {
                method: "POST",
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    email,
                    consent: true,
                    preferred_language: waitlistLanguage,
                }),
            });
            const data = await response
                .json()
                .catch(() => ({}));
            if (response.status === 409) {
                setLaunchInterest((current) => ({
                    interested_count: current?.interested_count || 0,
                    launch_date: current?.launch_date ||
                        "2026-12-13",
                    launch_status: "launched",
                    waitlist_open: false,
                }));
                setWaitlistMessage(ratingCopy.closed);
                return;
            }
            if (!response.ok) {
                const detail = typeof data?.detail === "string"
                    ? data.detail
                    : ratingCopy.requestFailed;
                setWaitlistMessage(localizeAuthResponse(detail, ratingCopy.requestFailed, copy));
                return;
            }
            setWaitlistMessage(localizeAuthResponse(data?.message, ratingCopy.verifyEmail, copy));
            setWaitlistEmail("");
            setWaitlistConsent(false);
            void loadLaunchInterest();
        }
        catch {
            setWaitlistMessage(ratingCopy.unavailable);
        }
        finally {
            setWaitlistBusy(false);
        }
    };
    const waitlistOpen = launchInterest?.waitlist_open ?? true;
    const summaryText = summary && summary.rating_count > 0
        ? `${summary.average.toLocaleString(copy.locale, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        })} ${ratingCopy.average} · ${summary.rating_count.toLocaleString(copy.locale)} ${summary.rating_count === 1
            ? ratingCopy.rating
            : ratingCopy.ratings}`
        : ratingCopy.noRatings;
    const launchInterestText = !launchInterest
        ? ratingCopy.loadingInterest
        : `${launchInterest.interested_count
            .toLocaleString(copy.locale)} ${launchInterest.interested_count === 1
            ? ratingCopy.personInterested
            : ratingCopy.peopleInterested}`;
    const launchDateText = (() => {
        const rawDate = launchInterest?.launch_date ||
            "2026-12-13";
        const date = new Date(`${rawDate}T00:00:00Z`);
        const formatted = Number.isNaN(date.getTime())
            ? rawDate
            : new Intl.DateTimeFormat(copy.locale, {
                dateStyle: "long",
                timeZone: "UTC",
            }).format(date);
        return `${ratingCopy.launchingPrefix} ${formatted}`;
    })();
    return (_jsxs("aside", { className: "avaaio-rating-card", "data-avaaio-build": AVAAIO_LOGIN_RATING_BUILD, "aria-label": ratingCopy.title, children: [_jsx("div", { className: "avaaio-rating-kicker", children: ratingCopy.community }), _jsx("h2", { className: "avaaio-rating-title", children: ratingCopy.title }), _jsx("div", { className: "avaaio-rating-stars", role: "group", "aria-label": ratingCopy.chooseRating, onMouseLeave: () => setHoveredRating(0), children: [1, 2, 3, 4, 5].map((rating) => {
                    const filled = rating <= activeRating;
                    return (_jsx("button", { type: "button", className: `avaaio-rating-star ${filled
                            ? "is-filled"
                            : ""}`, "aria-label": `${rating} ${rating === 1
                            ? ratingCopy.star
                            : ratingCopy.stars}: ${ratingCopy.labels[rating]}`, "aria-pressed": selectedRating === rating, disabled: busy, onMouseEnter: () => setHoveredRating(rating), onFocus: () => setHoveredRating(rating), onBlur: () => setHoveredRating(0), onClick: () => void chooseRating(rating), children: filled ? "★" : "☆" }, rating));
                }) }), "        ", _jsx("div", { className: "avaaio-rating-label", "aria-live": "polite", children: activeLabel }), _jsx("button", { type: "button", className: "avaaio-rating-submit", disabled: busy ||
                    selectedRating < 1 ||
                    selectedRating > 5, onClick: () => void submitSelectedRating(), children: busy
                    ? ratingCopy.submitting
                    : ratingCopy.submit }), waitlistOpen && (_jsxs("div", { className: "avaaio-launch-interest", "aria-live": "polite", children: [_jsx("div", { className: "avaaio-launch-count", children: launchInterestText }), _jsx("div", { className: "avaaio-launch-date", children: launchDateText }), _jsxs("form", { className: "avaaio-waitlist-form", onSubmit: submitWaitlist, children: [_jsx("label", { className: "avaaio-waitlist-label", htmlFor: "avaaio-waitlist-email", children: ratingCopy.joinLabel }), _jsx("input", { id: "avaaio-waitlist-email", className: "avaaio-waitlist-email", type: "email", inputMode: "email", autoComplete: "email", maxLength: 254, value: waitlistEmail, placeholder: ratingCopy.emailAddress, disabled: waitlistBusy, onChange: (event) => setWaitlistEmail(event.target.value) }), _jsx("label", { className: "avaaio-waitlist-language-label", htmlFor: "avaaio-waitlist-language", children: ratingCopy.preferredEmailLanguage }), _jsxs("select", { id: "avaaio-waitlist-language", className: "avaaio-waitlist-language", value: waitlistLanguage, disabled: waitlistBusy, onChange: (event) => setWaitlistLanguage(event.target.value), children: [_jsx("option", { value: "en", children: "English" }), _jsx("option", { value: "es", children: "Espa\u00F1ol" }), _jsx("option", { value: "fr", children: "Fran\u00E7ais" }), _jsx("option", { value: "de", children: "Deutsch" }), _jsx("option", { value: "it", children: "Italiano" }), _jsx("option", { value: "pt", children: "Portugu\u00EAs" })] }), _jsx("div", { className: "avaaio-waitlist-language-note", children: ratingCopy.bilingualNote }), _jsxs("label", { className: "avaaio-waitlist-consent", children: [_jsx("input", { type: "checkbox", checked: waitlistConsent, disabled: waitlistBusy, onChange: (event) => setWaitlistConsent(event.target.checked) }), _jsx("span", { children: ratingCopy.consent })] }), _jsx("button", { type: "submit", className: "avaaio-waitlist-submit", disabled: waitlistBusy ||
                                    !waitlistEmail.trim() ||
                                    !waitlistConsent, children: waitlistBusy
                                    ? ratingCopy.joining
                                    : ratingCopy.join })] }), waitlistMessage && (_jsx("div", { className: "avaaio-waitlist-message", role: "status", children: waitlistMessage }))] })), _jsxs("div", { className: "avaaio-rating-summary", children: [summary && summary.rating_count > 0 && (_jsx("div", { className: "avaaio-rating-overall-stars", role: "img", "aria-label": summaryText, children: [1, 2, 3, 4, 5].map((star) => {
                            const fill = Math.max(0, Math.min(1, summary.average - (star - 1)));
                            return (_jsxs("span", { className: "avaaio-rating-overall-star", "aria-hidden": "true", children: [_jsx("span", { className: "avaaio-rating-overall-star-empty", children: "\u2605" }), _jsx("span", { className: "avaaio-rating-overall-star-fill", style: {
                                            width: `${fill * 100}%`,
                                        }, children: "\u2605" })] }, star));
                        }) })), _jsx("div", { className: "avaaio-rating-summary-text", children: summaryText })] }), message && (_jsx("div", { className: "avaaio-rating-message", "aria-live": "polite", children: message })), _jsx("div", { className: "avaaio-rating-privacy", children: "No comments. One private rating per verified account." })] }));
};
export default AvaaioStarRating;
